import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronLeftIcon, Check } from "lucide-react";
import { Reactable } from "../../components/ui/reactable";
import { ContainerCard } from "../../components/ui/cards";
import { DEPARTMENT_WISE_ACTIVITY_REPORT, EMPLOYEE_DEPARTMENT } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import useApiMethods from "../../lib/useApiMethods";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const ACTIVITY_OPTIONS = [
    { value: "all", label: "All activities" },
    { value: "remark", label: "Remarks" },
    { value: "correction", label: "Corrections" },
    { value: "leave", label: "Leave / resignation" },
    { value: "profile", label: "Profile changes" },
];

const dropdownTriggerClass = cn(
    "inline-flex items-center justify-between gap-2 min-w-[220px]",
    "h-10 px-3 rounded-lg text-sm font-medium",
    "bg-muted/50 border border-[#0BB5AC]/40 text-foreground",
    "hover:bg-[#0BB5AC]/5 hover:border-[#0BB5AC]/60",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0BB5AC]/30"
);

const dropdownContentClass =
    "min-w-[240px] max-h-72 overflow-y-auto rounded-lg border border-[#0BB5AC]/20 bg-background p-1 shadow-lg";

const dropdownItemClass = (isActive) =>
    cn(
        "rounded-md px-3 py-2 text-sm cursor-pointer",
        isActive
            ? "bg-[#0BB5AC]/10 text-[#0BB5AC] font-medium"
            : "text-foreground hover:bg-[#0BB5AC]/5 hover:text-[#0BB5AC]"
    );

const EmployeeCell = ({ row, onNavigate }) => {
    const name = row?.employee_name || row?.original?.employee_name;
    const code = row?.employee_code || row?.original?.employee_code;
    const empId = row?.employee_id || row?.original?.employee_id;

    return (
        <button
            type="button"
            className="text-left hover:text-sidebar-primary transition-colors"
            onClick={() => empId && onNavigate(empId)}
            disabled={!empId}
        >
            <div className="font-medium text-gray-900 dark:text-white">{name || "-"}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{code || "-"}</div>
        </button>
    );
};

const DepartmentWiseActivityReport = () => {
    const navigate = useNavigate();
    const { fetch } = useApiMethods();
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [view, setView] = useState("detail");
    const [activityType, setActivityType] = useState("all");
    const [departments, setDepartments] = useState([]);
    const [departmentId, setDepartmentId] = useState("");

    const loadDepartments = useCallback(async () => {
        try {
            const res = await fetch(`${EMPLOYEE_DEPARTMENT}?drop_down=True`);
            if (res?.status === "success" && Array.isArray(res.data)) {
                setDepartments(res.data);
                if (res.data.length === 1) {
                    setDepartmentId(String(res.data[0].id));
                }
            }
        } catch {
            setDepartments([]);
        }
    }, [fetch]);

    useEffect(() => {
        loadDepartments();
    }, [loadDepartments]);

    const goToEmployee = useCallback((empId) => {
        navigate("/employee/viewDetails", { state: { employeeId: empId, activeTab: "activity" } });
    }, [navigate]);

    const viewFilter = useMemo(
        () => ({
            view,
            activity_type: activityType,
            ...(departmentId ? { department_id: departmentId } : {}),
        }),
        [view, activityType, departmentId]
    );

    const detailColumns = useMemo(
        () => [
            {
                accessor: "employee_name",
                Header: "Employee",
                filterable: true,
                fixed: true,
                maxWidth: "240px",
                Cell: ({ row }) => <EmployeeCell row={row?.original || row} onNavigate={goToEmployee} />,
            },
            { accessor: "department", Header: "Department", Cell: ({ value }) => value ?? "-" },
            {
                accessor: "activity_label",
                Header: "Activity",
                filterable: true,
                Cell: ({ value }) => (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
                        {value || "-"}
                    </span>
                ),
            },
            { accessor: "title", Header: "Title", Cell: ({ value }) => value ?? "-" },
            {
                accessor: "description",
                Header: "Details",
                Cell: ({ value }) => (
                    <span className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{value ?? "-"}</span>
                ),
            },
            { accessor: "actor_name", Header: "By", Cell: ({ value }) => value ?? "-" },
            { accessor: "occurred_at", Header: "When", Cell: ({ value }) => value ?? "-" },
        ],
        [goToEmployee]
    );

    const summaryColumns = useMemo(
        () => [
            { accessor: "department", Header: "Department", fixed: true },
            { accessor: "period", Header: "Period", Cell: ({ value }) => value ?? "-" },
            { accessor: "total_employees", Header: "Employees", Cell: ({ value }) => value ?? 0 },
            { accessor: "present_today", Header: "Present today", Cell: ({ value }) => value ?? 0 },
            { accessor: "absent_today", Header: "Absent today", Cell: ({ value }) => value ?? 0 },
            { accessor: "on_leave_today", Header: "On leave", Cell: ({ value }) => value ?? 0 },
            { accessor: "pending_corrections", Header: "Pending corrections", Cell: ({ value }) => value ?? 0 },
            { accessor: "total_activities", Header: "Activities", Cell: ({ value }) => value ?? 0 },
            { accessor: "remarks", Header: "Remarks", Cell: ({ value }) => value ?? 0 },
            { accessor: "corrections", Header: "Corrections", Cell: ({ value }) => value ?? 0 },
            { accessor: "leave_events", Header: "Leave events", Cell: ({ value }) => value ?? 0 },
        ],
        []
    );

    const employeesColumns = useMemo(
        () => [
            {
                accessor: "employee_name",
                Header: "Employee",
                filterable: true,
                fixed: true,
                Cell: ({ row }) => <EmployeeCell row={row?.original || row} onNavigate={goToEmployee} />,
            },
            { accessor: "designation", Header: "Designation", Cell: ({ value }) => value ?? "-" },
            {
                accessor: "today_status",
                Header: "Today",
                Cell: ({ value }) => (
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800">
                        {value ?? "-"}
                    </span>
                ),
            },
            { accessor: "last_activity_at", Header: "Last activity", Cell: ({ value }) => value ?? "-" },
            { accessor: "last_activity_type", Header: "Type", Cell: ({ value }) => value ?? "-" },
        ],
        [goToEmployee]
    );

    const columns = view === "summary"
        ? summaryColumns
        : view === "employees"
            ? employeesColumns
            : detailColumns;

    const selectedDept = departments.find((d) => String(d.id) === String(departmentId));
    const selectedActivity = ACTIVITY_OPTIONS.find((opt) => opt.value === activityType) || ACTIVITY_OPTIONS[0];

    return (
        <ContainerCard className="font-sans">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4 shrink-0">
                <span className="flex gap-2 items-center">
                    <ChevronLeftIcon onClick={() => navigate("/reports")} className="cursor-pointer" />
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                        Department Wise Activity ({totalRecords})
                    </h1>
                </span>

                <div className="flex flex-wrap items-center gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger className={dropdownTriggerClass}>
                            <span className="truncate">
                                {selectedDept?.department_name || "Select department"}
                            </span>
                            <ChevronDown className="h-4 w-4 shrink-0 text-[#0BB5AC]" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className={dropdownContentClass} sideOffset={6}>
                            {departments.map((dept) => {
                                const isActive = String(departmentId) === String(dept.id);
                                return (
                                    <DropdownMenuItem
                                        key={dept.id}
                                        isActive={isActive}
                                        onClick={() => setDepartmentId(String(dept.id))}
                                        className={dropdownItemClass(isActive)}
                                    >
                                        <span className="flex-1">{dept.department_name}</span>
                                        {isActive && <Check className="h-4 w-4 shrink-0 text-[#0BB5AC]" />}
                                    </DropdownMenuItem>
                                );
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {view === "detail" && (
                        <DropdownMenu>
                            <DropdownMenuTrigger className={dropdownTriggerClass}>
                                <span className="truncate">{selectedActivity.label}</span>
                                <ChevronDown className="h-4 w-4 shrink-0 text-[#0BB5AC]" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className={dropdownContentClass} sideOffset={6}>
                                {ACTIVITY_OPTIONS.map((opt) => {
                                    const isActive = activityType === opt.value;
                                    return (
                                        <DropdownMenuItem
                                            key={opt.value}
                                            isActive={isActive}
                                            onClick={() => setActivityType(opt.value)}
                                            className={dropdownItemClass(isActive)}
                                        >
                                            <span className="flex-1">{opt.label}</span>
                                            {isActive && <Check className="h-4 w-4 shrink-0 text-[#0BB5AC]" />}
                                        </DropdownMenuItem>
                                    );
                                })}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}

                    <SegmentSwitch
                        value={view}
                        onChange={setView}
                        options={[
                            { value: "summary", label: "Summary" },
                            { value: "detail", label: "Activity" },
                            { value: "employees", label: "Employees" },
                        ]}
                    />
                </div>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                Track remarks, corrections, leave, and profile changes grouped by department.
            </p>

            {!departmentId ? (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-gray-500 dark:text-gray-400">
                    Select a department to load activity data.
                </div>
            ) : (
                <Reactable
                    columns={columns}
                    data={data}
                    setData={setData}
                    setTotalCount={setTotalRecords}
                    url={DEPARTMENT_WISE_ACTIVITY_REPORT}
                    serverSide={true}
                    serverTrigger={`${view}-${activityType}-${departmentId}`}
                    filter={viewFilter}
                    printFilter={viewFilter}
                    rowSize="sm"
                    isCSVRequired={true}
                    isPDFRequired={true}
                    isFromToDateRequired={true}
                />
            )}
        </ContainerCard>
    );
}

export default DepartmentWiseActivityReport;
